import type { SettingsResponse } from "@/api/client";

/**
 * The COMPILED defaults of the operator-editable (whitelisted) params — the values baked into
 * application.yaml when no ConfigMap env / runtime override is set. Single source for the
 * "≠ default" drift markers (EffectiveRail + the strip counter). Mirrors the backend defaults;
 * ParamEditForm's per-field `def` fallbacks reference the same numbers.
 */
export const COMPILED_DEFAULTS = {
  slPoints: 1000,
  reinforceThresholdPoints: 75,
  trailDistancePoints: 75,
  maxSide: 3,
  cycleRiskPct: 0.2,
  maxTotalHard: 6,
  consecutiveStopLimit: 3,
  maxSpreadPoints: 7,
  flatWhenClosed: true,
  flattenLeadMinutes: 15,
  financingEnabled: true,
  financingBaseRatePct: 0.0193,
} as const;

export interface DriftEntry {
  key: string;
  effective: number | boolean;
  compiled: number | boolean;
}

/** Every whitelisted param whose effective value differs from its compiled default. */
export function settingsDrift(s: SettingsResponse): DriftEntry[] {
  const effective: Record<string, number | boolean | undefined> = {
    slPoints: s.twoTrail?.slPoints,
    reinforceThresholdPoints: s.twoTrail?.reinforceThresholdPoints,
    trailDistancePoints: s.twoTrail?.trailDistancePoints,
    maxSide: s.twoTrail?.maxSide,
    cycleRiskPct: s.liveSafety?.cycleRiskPct,
    maxTotalHard: s.positionLimits?.maxTotalHard,
    consecutiveStopLimit: s.positionLimits?.consecutiveStopLimit,
    maxSpreadPoints: s.stream?.maxSpreadPoints,
    flatWhenClosed: s.policy?.flatWhenClosed,
    flattenLeadMinutes: s.policy?.flattenLeadMinutes,
    financingEnabled: s.policy?.financingEnabled,
    financingBaseRatePct: s.policy?.financingBaseRatePct,
  };
  const out: DriftEntry[] = [];
  for (const [key, compiled] of Object.entries(COMPILED_DEFAULTS)) {
    const eff = effective[key];
    if (eff === undefined || eff === null) continue; // absent block (older backend) → unknown, not drift
    if (typeof compiled === "number" && typeof eff === "number") {
      if (Math.abs(eff - compiled) > 1e-9) out.push({ key, effective: eff, compiled });
    } else if (eff !== compiled) {
      out.push({ key, effective: eff as boolean, compiled: compiled as boolean });
    }
  }
  return out;
}

/** The compiled default for one key (drift-marker rendering). */
export function compiledOf(key: keyof typeof COMPILED_DEFAULTS): number | boolean {
  return COMPILED_DEFAULTS[key];
}
